import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, MessageCircle, Ticket, X as XIcon, Landmark, Copy } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CONTACT_INFO } from "@/data/anabiya";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Lazy-load 3rd party scripts */
function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-src="${src}"]`);
    if (existing) {
      // If the script tag exists AND already loaded (window[globalVar] set OR readyState complete)
      // resolve immediately; otherwise wait for its load/error.
      if (existing.dataset.loaded === "true") return resolve(true);
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.dataset.src = src;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = () => { s.dataset.loaded = "true"; resolve(true); };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

export const CheckoutPage = () => {
  useSmoothScroll();
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { code: currency, format, formatMoney } = useCurrency();

  const [buyer, setBuyer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    country: "",
  });
  const [cfg, setCfg] = useState(null);
  const [homeCfg, setHomeCfg] = useState({});

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [applied, setApplied] = useState(null); // {code, kind, value, discount, subtotal_after, currency}
  const [applying, setApplying] = useState(false);

  // ---- Server-authoritative quote — single source of truth for money ----
  // Every displayed and charged number below (subtotal, discount, total, PayPal)
  // is derived from this one call so the browser can never invent a total.
  // Kept in a state so we can render {subtotal, discount, total} verbatim.
  const [quote, setQuote] = useState(null); // {subtotal, discount, total, currency, coupon}
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)).catch(() => setCfg({ paypal: { enabled: false } }));
    axios.get(`${API}/home`).then((r) => setHomeCfg(r.data?.sections || {})).catch(() => {});
  }, []);

  // Re-quote whenever the cart, currency, or applied coupon change.
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    let cancelled = false;
    setQuoteLoading(true);
    axios.post(`${API}/payments/quote`, {
      items: items.map((it) => ({
        slug: it.slug, qty: it.qty, size: it.size, colour: it.colour,
        variation: it.variation || {}, summary: it.summary || "",
      })),
      currency,
      coupon_code: applied?.code || undefined,
    }).then(({ data }) => {
      if (cancelled) return;
      setQuote(data);
      // If the server rejected the coupon (e.g. min_subtotal not met after
      // a cart change) clear it locally so we don't send it to PayPal.
      if (applied && !data.coupon) setApplied(null);
    }).catch((e) => {
      if (cancelled) return;
      const msg = e?.response?.data?.detail;
      if (msg && applied) {
        toast.error(msg);
        setApplied(null);
      }
      setQuote(null);
    }).finally(() => { if (!cancelled) setQuoteLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, currency, applied?.code]);

  // Authoritative amounts (server-computed, correctly rounded, no browser math).
  const serverSubtotal = quote ? Number(quote.subtotal || 0) : 0;
  const discount = quote ? Number(quote.discount || 0) : 0;
  const grandTotal = quote ? Number(quote.total ?? Math.max(0, serverSubtotal - discount)) : Math.max(0, subtotal - 0);
  const money = (v) => formatMoney(v, currency);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return toast.error("Enter a coupon code");
    if (!items.length) return toast.error("Your cart is empty");
    setApplying(true);
    try {
      // The server-authoritative quote will re-run with the coupon and
      // return the exact discount and total. We just need to prove the
      // code is valid up-front so we can toast a friendly message.
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code, subtotal: serverSubtotal || subtotal, currency,
      });
      setApplied({ code: data.code, kind: data.kind, value: data.value, currency: data.currency });
      toast.success(`${code} applied — you saved ${money(data.discount)}`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Coupon invalid");
    } finally {
      setApplying(false);
    }
  };

  const clearCoupon = () => {
    setApplied(null);
    setCouponCode("");
  };

  const cartPayload = useMemo(
    () => ({
      items: items.map((it) => ({
        slug: it.slug, qty: it.qty, size: it.size, colour: it.colour,
        variation: it.variation || {}, summary: it.summary || "",
      })),
      currency,
      buyer,
      coupon_code: applied?.code || undefined,
      coupon_discount: discount || undefined,
      origin_url: window.location.origin,
    }),
    [items, currency, buyer, applied, discount]
  );

  const validate = () => {
    if (!items.length) {
      toast.error("Your cart is empty.");
      navigate("/shop");
      return false;
    }
    if (!buyer.name.trim() || !buyer.email.trim()) {
      toast.error("Please enter your name and email.");
      return false;
    }
    return true;
  };

  // Keep refs in sync with latest state so the PayPal button (rendered once)
  // always calls the current validate/cart/coupon.
  useEffect(() => { cartPayloadRef.current = cartPayload; }, [cartPayload]);
  useEffect(() => { appliedRef.current = applied; }, [applied]);
  useEffect(() => { validateRef.current = validate; });

  const paypalSupported = cfg?.paypal?.currencies?.includes(currency) ?? (currency !== "INR" && currency !== "AED");

  // ---- PayPal ----
  const [paypalStatus, setPaypalStatus] = useState("idle"); // idle | loading | ready | error | unavailable
  const paypalContainerRef = useRef(null);
  const paypalMounted = useRef(false);
  const paypalInstance = useRef(null);
  const cartPayloadRef = useRef(null);
  const validateRef = useRef(() => true);
  const appliedRef = useRef(null);

  useEffect(() => {
    // Wait for cfg to arrive
    if (!cfg) return;
    // Guard: if we've already mounted the buttons, do nothing on re-render (StrictMode-safe).
    if (paypalMounted.current) return;

    if (!cfg?.paypal?.enabled || !cfg.paypal.client_id) {
      setPaypalStatus("unavailable");
      console.warn("[PayPal] disabled — server config missing:", cfg?.paypal);
      return;
    }
    if (!paypalSupported) {
      setPaypalStatus("unavailable");
      console.warn(`[PayPal] currency ${currency} not supported by this account.`);
      return;
    }

    let cancelled = false;
    setPaypalStatus("loading");
    console.log("[PayPal] boot begin — cfg=", cfg?.paypal?.mode, "currency=", currency);

    const boot = async () => {
      try {
        const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
          cfg.paypal.client_id
        )}&currency=${encodeURIComponent(currency)}&intent=capture&components=buttons&enable-funding=venmo,paylater&disable-funding=credit`;
        console.log("[PayPal] loading SDK:", sdkUrl.replace(cfg.paypal.client_id, cfg.paypal.client_id.slice(0, 6) + "…"));
        await loadScript(sdkUrl);
        console.log("[PayPal] loadScript resolved. window.paypal=", !!window.paypal);
        if (cancelled) { console.log("[PayPal] cancelled after loadScript"); return; }
        if (!window.paypal) throw new Error("SDK loaded but window.paypal is missing");

        // Wait for container by ID (StrictMode-safe: id lookup finds it regardless of ref timing)
        let el = null;
        for (let i = 0; i < 40; i++) {
          el = paypalContainerRef.current || document.getElementById("paypal-btn-container");
          if (el) break;
          await new Promise((r) => setTimeout(r, 50));
        }
        if (cancelled) { console.log("[PayPal] cancelled during container wait"); return; }
        if (!el) throw new Error("Container never resolved");
        console.log("[PayPal] container found, calling Buttons()");

        if (paypalMounted.current) { console.log("[PayPal] already mounted, skipping"); return; }

        const instance = window.paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 48 },
          createOrder: async () => {
            if (!validateRef.current()) {
              console.warn("[PayPal] createOrder aborted — form invalid");
              throw new Error("Invalid form");
            }
            try {
              const { data } = await axios.post(`${API}/payments/paypal/create-order`, cartPayloadRef.current);
              window.__anabiya_order_id = data.order_id;
              console.log("[PayPal] order created:", data.paypal_order_id);
              return data.paypal_order_id;
            } catch (err) {
              console.error("[PayPal] createOrder network error:", err?.response?.status, err?.response?.data || err?.message);
              toast.error(err?.response?.data?.detail || "Could not start PayPal checkout. Try again in a moment.");
              throw err;
            }
          },
          onApprove: async (data) => {
            console.log("[PayPal] approved, capturing:", data.orderID);
            try {
              await axios.post(`${API}/payments/paypal/capture-order/${data.orderID}`);
              const orderId = window.__anabiya_order_id;
              if (appliedRef.current?.code) {
                axios.post(`${API}/coupons/${appliedRef.current.code}/use`).catch(() => {});
              }
              clear();
              navigate(`/payment/success?order_id=${orderId}&gateway=paypal`);
            } catch (err) {
              console.error("[PayPal] capture failed:", err?.response?.status, err?.response?.data || err?.message);
              toast.error("Payment received but capture failed. Please WhatsApp us with the PayPal receipt — we'll confirm your order manually.");
            }
          },
          onCancel: (data) => {
            console.log("[PayPal] cancelled by buyer:", data?.orderID || "(no id)");
          },
          onError: (err) => {
            const msg = String(err?.message || err || "");
            console.error("[PayPal] onError:", msg, err);
            if (/Window closed|popup close|Detected popup close|closed the PayPal window/i.test(msg)) {
              return; // silent — treat as cancel
            }
            toast.error("PayPal couldn't complete. Please retry, or WhatsApp us to place the order.");
          },
        });

        paypalInstance.current = instance;
        paypalMounted.current = true;
        try {
          await instance.render(el);
          // Once render() resolves the buttons ARE in the DOM — mark ready even if
          // StrictMode fired a cleanup (`cancelled=true`) in between. Not doing so
          // leaves the UI stuck on "Loading PayPal…" while the button is visible.
          setPaypalStatus("ready");
          console.log("[PayPal] buttons mounted ✓");
        } catch (renderErr) {
          console.error("[PayPal] render() rejected:", renderErr);
          paypalMounted.current = false;
          setPaypalStatus("error");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[PayPal] SDK boot failed:", err);
        setPaypalStatus("error");
      }
    };

    boot();

    return () => {
      cancelled = true;
      // NOTE: we deliberately do NOT tear down the buttons here. StrictMode invokes
      // this cleanup + re-runs the effect in dev; tearing down would trigger the
      // "container removed from DOM" error we're preventing. In production this
      // cleanup runs on unmount only, and PayPal's own logic handles that.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, currency, paypalSupported]);

  // ---- WhatsApp fallback ----
  const whatsappUrl = () => {
    const lines = [
      "*Order — Anabiya Decor*",
      `Buyer: ${buyer.name || "—"} · ${buyer.email || "—"} · ${buyer.phone || "—"}`,
      buyer.address ? `Ship to: ${buyer.address}${buyer.country ? ", " + buyer.country : ""}` : "",
      "",
      ...items.flatMap((it, i) => {
        const head = `${i + 1}. ${it.title} × ${it.qty} — ${format(it.price * it.qty)}`;
        return it.summary ? [head, `    ${it.summary}`] : [head];
      }),
      "",
      `Subtotal (${currency}): ${format(subtotal)}`,
    ].filter(Boolean);
    return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <main data-testid="checkout-page" className="bg-white min-h-screen">
      <Seo path="/checkout" title="Secure Checkout" noindex />
      <AnnouncementBar />
      <Navbar />

      <section className="px-6 md:px-12 lg:px-16 py-10 md:py-16 border-b border-hairline bg-bone">
        <div className="overline text-forest/60 mb-4 flex items-center gap-2">
          <Link to="/" className="hover:text-forest transition-colors">Home</Link>
          <span aria-hidden>/</span>
          <Link to="/shop" className="hover:text-forest transition-colors">Shop</Link>
          <span aria-hidden>/</span>
          <span className="text-forest">Checkout</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-display text-forest text-5xl md:text-7xl leading-[0.9] tracking-tight"
        >
          Checkout <span className="italic text-gold">quietly.</span>
        </motion.h1>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* Left: buyer + payment */}
        <div className="lg:col-span-7 min-w-0 space-y-12">
          <div>
            <div className="overline text-gold mb-5">§ 01 · Contact</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {[
                ["name", "Name", "text", true],
                ["email", "Email", "email", true],
                ["phone", "Phone (with country code)", "tel", false],
                ["country", "Country", "text", false],
              ].map(([k, label, type, req]) => (
                <div key={k} className="col-span-2 md:col-span-1">
                  <label className="overline text-forest/60">{label}{req && " *"}</label>
                  <input
                    data-testid={`checkout-${k}`}
                    type={type}
                    required={req}
                    value={buyer[k]}
                    onChange={(e) => setBuyer((b) => ({ ...b, [k]: e.target.value }))}
                    className="w-full bg-transparent border-b border-hairline focus:border-forest outline-none py-3 text-forest transition-colors duration-500"
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="overline text-forest/60">Shipping Address</label>
                <textarea
                  data-testid="checkout-address"
                  rows={2}
                  value={buyer.address}
                  onChange={(e) => setBuyer((b) => ({ ...b, address: e.target.value }))}
                  className="w-full bg-transparent border-b border-hairline focus:border-forest outline-none py-3 text-forest resize-none transition-colors duration-500"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="overline text-gold mb-5">§ 02 · Payment</div>
            <p className="text-forest/70 mb-6">
              You&rsquo;re paying in <span className="font-display text-forest text-lg">{currency}</span>. PayPal &amp; guest card checkout supported across USA, UK, Canada, Australia, Japan, Hong Kong, UAE, Singapore &amp; EU.
            </p>

            <div className="space-y-4">
              {/* PayPal (with guest card option) */}
              <div className="border-hairline p-5 md:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-display italic text-gold text-lg">PayPal</span>
                  <div className="font-display text-forest text-xl">Pay with PayPal or Card</div>
                </div>
                <p className="text-forest/60 text-sm mb-4">
                  Fastest international checkout — pay with PayPal balance, Visa, Mastercard, Amex or Discover. No PayPal account required, buyer protection included.
                </p>

                {/* Container is ALWAYS in the DOM and visible so PayPal's async
                    render never loses it, even under React StrictMode. */}
                <div
                  ref={paypalContainerRef}
                  id="paypal-btn-container"
                  data-testid="paypal-btn-container"
                  className="min-h-[56px]"
                />

                {(paypalStatus === "loading" || paypalStatus === "idle") && (
                  <div
                    data-testid="pay-paypal-loading"
                    className="w-full inline-flex items-center justify-center gap-3 border border-hairline text-forest/60 overline px-6 py-4 rounded-full mt-3"
                  >
                    <span className="w-3 h-3 rounded-full border-2 border-forest/40 border-t-forest animate-spin" />
                    Loading PayPal…
                  </div>
                )}

                {(paypalStatus === "unavailable" || paypalStatus === "error") && (
                  <a
                    href={whatsappUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pay-paypal-unavailable"
                    className="w-full inline-flex items-center justify-center gap-3 bg-neutral-900 text-white overline px-6 py-4 rounded-full hover:bg-neutral-800 transition-colors duration-500 mt-3"
                  >
                    <Lock size={13} strokeWidth={1.6} />
                    PayPal unavailable — Order via WhatsApp
                  </a>
                )}
              </div>

              {/* WhatsApp fallback */}
              <div className="pt-3">
                <a
                  data-testid="pay-whatsapp"
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-3 border border-forest/70 text-forest overline px-6 py-4 rounded-full hover:bg-forest hover:text-white transition-colors duration-500"
                >
                  <MessageCircle size={13} strokeWidth={1.6} /> Order via WhatsApp instead
                </a>
                <p className="mt-2 text-center text-forest/50 text-xs">
                  Bank transfer, custom invoices and wholesale POs handled personally.
                </p>
              </div>

              {/* Payoneer / Bank Transfer */}
              {(homeCfg?.payoneer?.enabled) && (
                <div className="border-hairline p-5 md:p-6" data-testid="pay-payoneer-block">
                  <div className="flex items-center gap-3 mb-3">
                    <Landmark size={18} strokeWidth={1.4} className="text-gold" />
                    <div className="font-display text-forest text-xl">{homeCfg.payoneer.title || "Pay by Bank Transfer (Payoneer)"}</div>
                  </div>
                  <p className="text-forest/60 text-sm mb-4">
                    {homeCfg.payoneer.subtitle || "For USA / UK / EU / AU buyers who prefer a direct bank transfer to our Payoneer receiving account. We'll email an invoice once you send us your details on WhatsApp."}
                  </p>
                  <div className="bg-cream/60 border border-hairline rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {["beneficiary","bank_name","account_number","routing_number","swift","iban","branch_address","reference"].map((k) => {
                      const v = homeCfg.payoneer[k];
                      if (!v) return null;
                      const label = k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
                      return (
                        <div key={k} className="flex items-start justify-between gap-3 border-b border-hairline/60 pb-2" data-testid={`payoneer-${k}`}>
                          <div className="flex-1 min-w-0">
                            <div className="overline text-forest/50 text-[10px] tracking-[2px]">{label}</div>
                            <div className="font-display text-forest tabnum truncate">{v}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); }}
                            className="p-2 text-forest/60 hover:text-forest"
                            aria-label={`Copy ${label}`}
                            data-testid={`payoneer-copy-${k}`}
                          >
                            <Copy size={12} strokeWidth={1.8} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-forest/50 text-xs italic">
                    After the transfer, share the receipt with us on WhatsApp so we can begin production. Amount to transfer: <span className="tabnum text-forest">{money(grandTotal)}</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: order summary */}
        <aside className="lg:col-span-5 min-w-0 lg:sticky lg:top-24 lg:self-start">
          <div className="bg-bone border-hairline p-6 md:p-8">
            <div className="overline text-gold mb-5">§ Order Summary</div>
            {items.length === 0 ? (
              <p className="text-forest/70">Your cart is empty. <Link to="/shop" className="edit-link text-forest">Browse the shop →</Link></p>
            ) : (
              <>
                <ul className="divide-y divide-hairline">
                  {items.map((it, idx) => {
                    // Prefer the server's priced line (correctly rounded in the
                    // display currency) so cart display matches PayPal exactly.
                    const priced = quote?.items_priced?.[idx];
                    const lineTotal = priced?.line_total ?? null;
                    return (
                    <li key={`${it.slug}-${it.summary || ""}`} className="py-4 flex gap-4">
                      <div className="w-14 h-16 shrink-0 overflow-hidden clip-slab bg-white">
                        <img src={it.image} alt={it.title ? `${it.title} — cart item` : "Cart item"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-forest text-base leading-tight truncate">{it.title}</div>
                        <div className="overline text-forest/50 mt-1 whitespace-normal break-words">
                          {(it.summary || [it.size, it.colour].filter(Boolean).join(" · ") || "—") + " · × " + it.qty}
                        </div>
                      </div>
                      <div className="font-display text-forest tabnum text-sm">
                        {lineTotal != null ? money(lineTotal) : format(it.price * it.qty)}
                      </div>
                    </li>
                    );
                  })}
                </ul>
                <div className="mt-5 pt-5 border-t border-hairline">
                  <div className="flex items-baseline justify-between">
                    <span className="overline text-forest/60">Subtotal · {currency}</span>
                    <span className="font-display text-forest text-2xl tabnum" data-testid="checkout-subtotal">{money(serverSubtotal)}</span>
                  </div>

                  {/* Coupon input */}
                  <div className="mt-5" data-testid="checkout-coupon-block">
                    <div className="overline text-forest/60 mb-2 flex items-center gap-1">
                      <Ticket size={11} strokeWidth={1.8} /> Discount code
                    </div>
                    {applied && quote?.coupon ? (
                      <div className="flex items-center justify-between gap-2 bg-forest text-champagne rounded-lg px-3 py-2.5" data-testid="checkout-coupon-applied">
                        <div className="flex-1 min-w-0">
                          <div className="font-display tracking-widest">{quote.coupon.code}</div>
                          <div className="text-champagne/70 text-[11px]">
                            {quote.coupon.kind === "percent" ? `${quote.coupon.value}% off` : `${money(quote.coupon.value)} off`} · you save {money(discount)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearCoupon}
                          className="p-1.5 hover:bg-white/10 rounded-full"
                          aria-label="Remove coupon"
                          data-testid="checkout-coupon-clear"
                        >
                          <XIcon size={13} strokeWidth={1.8} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-stretch gap-2 min-w-0">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                          placeholder="WELCOME10"
                          className="flex-1 min-w-0 bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest tracking-wider focus:outline-none focus:border-forest"
                          data-testid="checkout-coupon-input"
                          autoCorrect="off"
                          autoCapitalize="characters"
                          spellCheck={false}
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={applying || !couponCode.trim()}
                          className="shrink-0 whitespace-nowrap overline text-white bg-forest rounded-full px-4 sm:px-5 py-2 text-xs tracking-[2px] disabled:opacity-40 hover:bg-[hsl(150_60%_16%)] transition-colors duration-500"
                          data-testid="checkout-coupon-apply"
                        >
                          {applying ? "…" : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>

                  {discount > 0 && (
                    <div className="mt-4 flex items-baseline justify-between text-gold" data-testid="checkout-discount-row">
                      <span className="overline">Discount</span>
                      <span className="font-display tabnum">− {money(discount)}</span>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-hairline flex items-baseline justify-between">
                    <span className="overline text-forest">Total · {currency}</span>
                    <span className="font-display text-forest text-3xl tabnum" data-testid="checkout-grand-total">{money(grandTotal)}</span>
                  </div>
                </div>
                <p className="mt-2 text-forest/50 text-xs">Taxes and shipping calculated on confirmation.</p>

                <div className="mt-6 flex items-start gap-3 text-forest/70 text-xs">
                  <ShieldCheck size={16} strokeWidth={1.4} className="text-gold mt-0.5 shrink-0" />
                  <span>SSL-secured checkout. We never store your card. Verified by PayPal.</span>
                </div>
              </>
            )}
          </div>
        </aside>
      </section>

      <Footer />
      <FloatingButtons />
    </main>
  );
};
